/**
 * Flat list of Radar singletons (Items sin agrupar) with expand / create / add-to-context.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { AttentionItem, SourceType } from '../../domain/types';
import { buildCrossAiSummary, type WorkThread } from '../../domain/radarThreads';
import { resourceIdForAttentionItem } from '../../domain/workContextBridge';
import { BrandIcon } from '../../ui/common';
import { relationToJiraIssue, type ResourceRelation } from '@zunelo/shared';
import { SOURCE_PROVIDERS } from '../../domain/providerRegistry';
import type { ConnectableSource } from '../../domain/providerIds';

const JIRA_KEY_RE = /^[A-Z][A-Z0-9]+-\d+$/;

/** Internal-navigation label ("Ver en Tareas/Mensajes/Código/Correo") for the
 * ungrouped-item action, which always navigates within Zunelo — never the
 * "Abrir en <Proveedor>" external-navigation phrasing. See
 * openspec/changes/provider-item-interaction-ux (honest controls). */
export function internalNavLabel(source: string): string {
  const def = SOURCE_PROVIDERS[source as ConnectableSource];
  return def ? `Ver en ${def.navLabel}` : `Ver en ${providerLabel(source)}`;
}

export function attentionItemForThread(
  thread: WorkThread,
  items: readonly AttentionItem[],
): AttentionItem | undefined {
  const fromAction = thread.actions[0]?.targetId;
  if (fromAction) {
    const hit = items.find((item) => item.id === fromAction);
    if (hit) return hit;
  }
  return items.find(
    (item) =>
      item.id === thread.id ||
      item.primary === thread.key ||
      item.ticket === thread.key ||
      item.primary === thread.id,
  );
}

export function providerLabel(source: string): string {
  if (source === 'jira') return 'Jira';
  if (source === 'github') return 'GitHub';
  if (source === 'slack') return 'Slack';
  if (source === 'agents') return 'Agentes';
  return source;
}

export function buildAttachRelation(
  item: AttentionItem,
  context: WorkThread,
): { kind: 'hub'; hubId: string } | { kind: 'relation'; relation: ResourceRelation } {
  const memberId = resourceIdForAttentionItem(item);
  if (context.isManual) {
    return { kind: 'hub', hubId: context.id };
  }
  const observedAt = new Date().toISOString();
  if (JIRA_KEY_RE.test(context.key)) {
    const type =
      item.source === 'github' ? 'implements' : item.source === 'slack' ? 'discusses' : 'related';
    return {
      kind: 'relation',
      relation: relationToJiraIssue(
        memberId,
        context.key,
        type,
        observedAt,
        `radar-attach:${item.id}:${context.key}`,
        'confirmed',
      ),
    };
  }
  return { kind: 'hub', hubId: context.id };
}

interface RadarUngroupedItemsSectionProps {
  threads: WorkThread[];
  contexts: WorkThread[];
  items: AttentionItem[];
  onRefresh: () => void;
  onCreateContext: (thread: WorkThread, item: AttentionItem | undefined) => Promise<void>;
  onAttachToContext: (item: AttentionItem, target: WorkThread) => Promise<boolean>;
  onNavigate?: (thread: WorkThread) => void;
}

function sourceForThread(thread: WorkThread): SourceType | 'agents' {
  const raw = thread.sources[0]?.source;
  if (
    raw === 'jira' ||
    raw === 'github' ||
    raw === 'slack' ||
    raw === 'gmail' ||
    raw === 'agents'
  ) {
    return raw;
  }
  return 'jira';
}

function AddToContextPanel({
  threadId,
  contexts,
  busy,
  onAttach,
  onCreate,
  onClose,
}: {
  threadId: string;
  contexts: WorkThread[];
  busy: boolean;
  onAttach: (ctx: WorkThread) => void;
  onCreate: () => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDoc = (event: MouseEvent) => {
      if (!panelRef.current?.contains(event.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);

  const filteredContexts = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return contexts;
    return contexts.filter(
      (ctx) => ctx.key.toLowerCase().includes(q) || ctx.title.toLowerCase().includes(q),
    );
  }, [contexts, query]);

  return (
    <div
      ref={panelRef}
      data-testid="radar-add-to-context-menu"
      role="menu"
      className="z-radar-ungrouped-add-panel"
    >
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
      >
        <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--z-text)' }}>
          Añadir a contexto
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: 'var(--z-dim)',
          }}
        >
          ×
        </button>
      </div>
      <label className="z-radar-ungrouped-search">
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          style={{ color: 'var(--z-dim)' }}
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          data-testid="radar-add-to-context-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar contexto…"
        />
      </label>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxHeight: 180,
          overflowY: 'auto',
        }}
      >
        {filteredContexts.length === 0 ? (
          <span style={{ fontSize: 12, color: 'var(--z-dim)', padding: '6px 4px' }}>
            No hay contextos coincidentes.
          </span>
        ) : (
          filteredContexts.map((ctx) => (
            <button
              key={ctx.id}
              type="button"
              role="menuitem"
              data-testid={`radar-add-to-context-${ctx.id}`}
              disabled={busy}
              onClick={() => onAttach(ctx)}
              className="z-radar-ungrouped-add-option"
            >
              <strong>{ctx.key}</strong>
              <span> · {ctx.title}</span>
            </button>
          ))
        )}
      </div>
      <button
        type="button"
        data-testid={`radar-add-to-context-create-${threadId}`}
        disabled={busy}
        onClick={onCreate}
        className="z-radar-ungrouped-create-link"
      >
        + Crear nuevo contexto
      </button>
    </div>
  );
}

export const RadarUngroupedItemsSection: React.FC<RadarUngroupedItemsSectionProps> = ({
  threads,
  contexts,
  items,
  onRefresh,
  onCreateContext,
  onAttachToContext,
  onNavigate,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [addMenuThreadId, setAddMenuThreadId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  if (threads.length === 0) return null;

  return (
    <section
      data-testid="radar-ungrouped-items"
      className="z-radar-ungrouped"
      style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h3
              style={{
                margin: 0,
                fontSize: 14,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: 'var(--z-text)',
              }}
            >
              Items sin agrupar
            </h3>
            <span
              data-testid="radar-ungrouped-count"
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--z-dim)',
                background: 'var(--z-fill)',
                borderRadius: 999,
                padding: '2px 8px',
              }}
            >
              {threads.length}
            </span>
          </div>
          <span style={{ fontSize: 12, color: 'var(--z-dim)' }}>
            Señales que aún no están en un contexto. Expándelas para entenderlas y agruparlas.
          </span>
        </div>
        <button
          type="button"
          data-testid="radar-ungrouped-refresh"
          onClick={onRefresh}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            border: 'none',
            background: 'transparent',
            color: 'var(--z-accent)',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          >
            <path d="M21 12a9 9 0 1 1-2.6-6.3" />
            <polyline points="21 3 21 9 15 9" />
          </svg>
          Actualizar
        </button>
      </div>

      <div className="z-radar-ungrouped-list">
        {threads.map((thread) => {
          const source = sourceForThread(thread);
          const item = attentionItemForThread(thread, items);
          const preview = thread.aiDiagnostic || thread.title || thread.description || '';
          const isExpanded = expandedId === thread.id;
          const isAddOpen = addMenuThreadId === thread.id;
          const summary = buildCrossAiSummary(thread);

          return (
            <article
              key={thread.id}
              data-testid={`radar-ungrouped-row-${thread.id}`}
              className={`z-radar-ungrouped-card${isExpanded ? ' is-expanded' : ''}`}
              aria-expanded={isExpanded}
            >
              <button
                type="button"
                className="z-radar-ungrouped-card-header"
                data-testid={`radar-ungrouped-toggle-${thread.id}`}
                onClick={() => {
                  setExpandedId((current) => (current === thread.id ? null : thread.id));
                  setAddMenuThreadId(null);
                }}
              >
                <BrandIcon source={source} size={18} />
                <div className="z-radar-ungrouped-card-heading">
                  <div className="z-radar-ungrouped-card-title-row">
                    <span className="z-radar-ungrouped-key">{thread.key}</span>
                    <span className="z-radar-ungrouped-provider">· {providerLabel(source)}</span>
                  </div>
                  {!isExpanded && <span className="z-radar-ungrouped-preview">{preview}</span>}
                </div>
                <span className="z-radar-ungrouped-age">{thread.updatedText}</span>
                <span className="z-radar-ungrouped-chevron" aria-hidden>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                  >
                    {isExpanded ? (
                      <polyline points="18 15 12 9 6 15" />
                    ) : (
                      <polyline points="6 9 12 15 18 9" />
                    )}
                  </svg>
                </span>
              </button>

              {isExpanded && (
                <div
                  className="z-radar-ungrouped-card-body"
                  data-testid={`radar-ungrouped-expanded-${thread.id}`}
                >
                  <h4 className="z-radar-ungrouped-card-title">{thread.title}</h4>
                  <div>
                    <span className="z-radar-context-summary-label">Resumen (IA)</span>
                    <p>{summary}</p>
                  </div>
                  {thread.nextStep && (
                    <div className="z-radar-context-next">
                      <strong>Qué sigue</strong>
                      <span aria-hidden>→</span>
                      <span>{thread.nextStep}</span>
                    </div>
                  )}
                  {thread.sources[0]?.detail && (
                    <p className="z-radar-ungrouped-detail">{thread.sources[0].detail}</p>
                  )}

                  <div className="z-radar-ungrouped-actions">
                    {onNavigate && (
                      <button
                        type="button"
                        data-testid={`radar-ungrouped-open-${thread.id}`}
                        onClick={() => onNavigate(thread)}
                        className="z-radar-ungrouped-action"
                      >
                        {internalNavLabel(source)}
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`radar-ungrouped-create-${thread.id}`}
                      disabled={busyId === thread.id}
                      onClick={() => {
                        setBusyId(thread.id);
                        void onCreateContext(thread, item).finally(() => setBusyId(null));
                      }}
                      className="z-radar-ungrouped-action z-radar-ungrouped-action--primary"
                    >
                      Crear contexto
                    </button>
                    <div style={{ position: 'relative' }}>
                      <button
                        type="button"
                        data-testid={`radar-ungrouped-menu-${thread.id}`}
                        aria-expanded={isAddOpen}
                        disabled={!item || busyId === thread.id}
                        onClick={() => setAddMenuThreadId(isAddOpen ? null : thread.id)}
                        className="z-radar-ungrouped-action"
                      >
                        Añadir a contexto
                      </button>
                      {isAddOpen && item && (
                        <AddToContextPanel
                          threadId={thread.id}
                          contexts={contexts}
                          busy={busyId === thread.id}
                          onClose={() => setAddMenuThreadId(null)}
                          onAttach={(ctx) => {
                            setBusyId(thread.id);
                            void onAttachToContext(item, ctx).then((ok) => {
                              setBusyId(null);
                              if (ok) setAddMenuThreadId(null);
                            });
                          }}
                          onCreate={() => {
                            setAddMenuThreadId(null);
                            setBusyId(thread.id);
                            void onCreateContext(thread, item).finally(() => setBusyId(null));
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
};

/** Exported for unit tests — attach payload builder does not touch React. */
export type AttachPlan = ReturnType<typeof buildAttachRelation>;
